import { Link } from 'react-router-dom'

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/login" className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1">
          ← Back
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-xl">🍴</span>
          <h1 className="font-semibold text-gray-900">Dinner Planner</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10 text-gray-700">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-400 mb-8">Last updated: June 25, 2026</p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Overview</h2>
          <p className="leading-relaxed">
            Dinner Planner (<strong>dinner.maksimovich.org</strong>) is a private, invitation-only family
            application operated by Andrew Maksimovich for personal use by members of the Maksimovich
            household. This application is not open to the public and has no advertising, no data
            monetization, and no third-party analytics. This privacy policy describes how the application
            collects, uses, and stores information.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Who Can Access This App</h2>
          <p className="leading-relaxed">
            Access is restricted to a small number of named individuals who have been given credentials
            directly by the application owner. There is no public registration or sign-up process.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Information We Collect</h2>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">Account Information</h3>
          <p className="leading-relaxed">
            User accounts (name, username, hashed password) are created manually by the application owner
            and stored in AWS DynamoDB. We do not collect email addresses, phone numbers, or any
            personally identifying information beyond a display name chosen by the owner.
          </p>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">Passkey / Biometric Credentials</h3>
          <p className="leading-relaxed">
            If you register a passkey (Face ID or Touch ID), the cryptographic public key credential is
            stored in AWS DynamoDB. Your biometric data never leaves your device. Apple handles Face ID
            locally and only shares a cryptographic signature with the app. We never have access to your
            fingerprint or face data.
          </p>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">Meal and Calendar Data</h3>
          <p className="leading-relaxed">
            Meal names, descriptions, notes, recipe URLs, and calendar entries you create are stored in
            AWS DynamoDB. This data is private to the household and is not shared with anyone outside
            the app.
          </p>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">Recipe Content</h3>
          <p className="leading-relaxed">
            When you use Search online recipes or Import from URL, the app fetches content from
            third-party recipe websites and sends that content to the Anthropic API to extract structured
            recipe data. The extracted recipe is stored in DynamoDB; raw scraped content is not retained.
          </p>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">Restaurant Search</h3>
          <p className="leading-relaxed">
            When you search for restaurants, your search query is sent to the Google Places API.
            No location data is collected from your device.
          </p>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">SMS Notifications</h3>
          <p className="leading-relaxed">
            The app may send SMS messages to household members via AWS SNS when a meal proposal has been
            pending for 3 or more hours. Phone numbers are stored in application configuration and are
            not shared with third parties beyond AWS SNS for delivery.
          </p>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">Pinterest Integration</h3>
          <p className="leading-relaxed">
            If you connect a Pinterest account, the app requests read-only access to your Pinterest
            boards to display recipe pins. The app stores a Pinterest access token in DynamoDB to
            maintain your session. The app does not post to Pinterest or modify your boards. You can
            revoke access at any time through your Pinterest account settings.
          </p>

          <h3 className="font-semibold text-gray-800 mt-4 mb-2">Log Data</h3>
          <p className="leading-relaxed">
            AWS automatically logs Lambda invocations, API Gateway requests, and CloudFront access. These
            logs include IP addresses, timestamps, and HTTP status codes, and are used only for debugging.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">How We Use Your Information</h2>
          <p className="leading-relaxed mb-3">We use collected information solely to:</p>
          <ul className="list-disc pl-6 space-y-2 leading-relaxed">
            <li>Authenticate you and maintain your session</li>
            <li>Display and store meal and calendar data you create</li>
            <li>Fetch and parse recipe content you request</li>
            <li>Send SMS notifications about pending meal proposals</li>
            <li>Display Pinterest recipe pins if you connect your account</li>
            <li>Debug errors and maintain the application</li>
          </ul>
          <p className="leading-relaxed mt-3">
            We do not use your information for advertising, analytics, profiling, or any commercial purpose.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Services</h2>
          <p className="leading-relaxed mb-3">The application uses the following third-party services:</p>
          <ul className="list-disc pl-6 space-y-2 leading-relaxed">
            <li><strong>Amazon Web Services</strong> — hosting, database, SMS, scheduled tasks. <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">Privacy Policy</a></li>
            <li><strong>Anthropic</strong> — AI recipe parsing. Recipe text is sent to their API. <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">Privacy Policy</a></li>
            <li><strong>SerpAPI</strong> — recipe web search. <a href="https://serpapi.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">Privacy Policy</a></li>
            <li><strong>Google Places API</strong> — restaurant search. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">Privacy Policy</a></li>
            <li><strong>Pinterest API</strong> — optional recipe board browsing. <a href="https://policy.pinterest.com/en/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">Privacy Policy</a></li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Data Storage and Security</h2>
          <p className="leading-relaxed">
            All data is stored in AWS DynamoDB in us-east-1. Data is encrypted at rest and in transit.
            The app uses HTTPS exclusively. Passwords are hashed with bcrypt. Passkey credentials are
            stored as public keys only; private keys never leave your device.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Data Retention and Deletion</h2>
          <p className="leading-relaxed">
            Meal and calendar data is retained until you delete it within the app. To request deletion
            of your account and all associated data, contact the application owner directly.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact</h2>
          <p className="leading-relaxed">
            For questions about this policy or to request data deletion:<br />
            <strong>Andrew Maksimovich</strong><br />
            <a href="mailto:andrew.maksimovich@gmail.com" className="text-green-600 underline">andrew.maksimovich@gmail.com</a>
          </p>
        </section>

        <div className="border-t border-gray-200 pt-6 mt-10">
          <Link to="/login" className="text-sm text-gray-400 hover:text-gray-600">
            ← Back to Dinner Planner
          </Link>
        </div>
      </div>
    </div>
  )
}
